<template>
  <div
    :class="`flex w-auto flex-grow py-2 bg-none flex-row justify-center lg:justify-end`"
  >
    <!-- Toggle Button (only shown when toggleable is true) -->
    <button
      v-if="toggleable && !isExpanded"
      @click="toggleSearch"
      class="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
      :class="toggleButtonClass"
      aria-label="Open search"
    >
      <v-icon name="hi-search" :class="toggleIconClass"></v-icon>
    </button>

    <!-- Search Input Container -->
    <div
      v-show="!toggleable || isExpanded"
      class="w-full flex flex-row justify-center items-center bg-white rounded p-1 relative search-box-container"
      :class="{ 'animate-fadeIn': toggleable && isExpanded }"
      :style="borderColor ? { border: `1px solid ${borderColor}` } : {}"
    >
      <input
        v-model="searchValue"
        type="text"
        placeholder="Search..."
        class="py-1 pl-2 rounded text-black w-full h-full bg-white focus:outline-none p-0 m-0 text-normal"
        @input="handleInput"
        @focus="showResults = true"
        @blur="handleBlur"
        @keydown="handleKeydown"
        ref="searchInput"
      />

      <!-- Search Results Dropdown -->
      <div
        v-if="showResults && results.length > 0"
        class="absolute top-full left-0 right-0 mt-1 rounded-lg shadow-xl flex flex-col overflow-y-auto max-h-96 bg-white z-50"
      >
        <div
          v-for="(result, index) in results"
          :key="index"
          class="flex flex-col justify-start items-start px-2 pt-2 hover:bg-blue-50 cursor-pointer"
          :class="{ 'bg-blue-50': selectedIndex === index }"
          @click="
            (e: MouseEvent) => {
              const target = e.target as HTMLElement;
              const anchor = (e.currentTarget as HTMLElement).querySelector(
                'a',
              );
              const clickedOnAnchor =
                anchor && (target === anchor || anchor.contains(target));

              // Only call selectResult if not clicking on the anchor
              // Let the anchor handle its own navigation
              if (!clickedOnAnchor) {
                selectResult(result);
              }
            }
          "
          @mouseenter="selectedIndex = index"
        >
          <a
            :href="result.product_url"
            class="w-full"
            @mousedown="navigateToResult(result.product_url)"
          >
            <div
              class="flex flex-row justify-start items-center py-2 text-slate-900"
            >
              <img
                :src="result.image_url"
                :alt="result.shopfront_name"
                class="w-12 h-12 mr-2"
              />
              <p>{{ result.shopfront_name }}</p>
            </div>
            <span
              v-if="index !== lastIndex"
              class="border-b border-gray-200 w-full h-2 pb-2"
            ></span>
          </a>
        </div>
      </div>

      <!-- No Results Message -->
      <div
        v-if="
          showResults &&
          results.length === 0 &&
          searchValue.length > 0 &&
          !loading
        "
        class="absolute top-full left-0 right-0 mt-1 rounded-lg shadow-xl bg-white z-50 text-slate-900"
      >
        <div class="flex flex-row p-4">
          <p>No results found</p>
        </div>
      </div>

      <!-- Loading Indicator -->
      <div
        v-if="loading"
        class="absolute top-full left-0 right-0 mt-1 rounded-lg shadow-xl bg-white z-50 text-slate-900"
      >
        <div class="flex flex-row p-4">
          <p>Loading...</p>
        </div>
      </div>

      <button
        class="p-2 rounded text-white bg-primary"
        @click="toggleable && isExpanded ? toggleSearch() : undefined"
        :aria-label="toggleable && isExpanded ? 'Close search' : 'Search'"
      >
        <v-icon
          :name="
            toggleable && isExpanded ? 'la-times-solid' : 'la-search-solid'
          "
        ></v-icon>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import Axios from "axios";
import { ref, defineComponent, computed, nextTick } from "vue";

export default defineComponent({
  props: {
    toggleable: {
      type: Boolean,
      default: false,
    },
    toggleButtonClass: {
      type: String,
      default: "bg-sky-50 hover:bg-sky-100",
    },
    toggleIconClass: {
      type: String,
      default: "w-6 h-6 text-blue-600 bg-red",
    },
    borderColor: {
      type: String,
      default: "",
    },
  },
  setup(props) {
    const results = ref<any[]>([]);
    const searchValue = ref<string>("");
    const showResults = ref(false);
    const loading = ref(false);
    const selectedIndex = ref(-1);
    const searchInput = ref<HTMLInputElement | null>(null);
    const isExpanded = ref(false);
    const isClickingResult = ref(false);

    const lastIndex = computed(() => results.value.length - 1);

    const navigateToResult = (url: string) => {
      isClickingResult.value = true;
      (globalThis as any).location.href = url;
    };

    const toggleSearch = async () => {
      isExpanded.value = !isExpanded.value;

      if (isExpanded.value) {
        // Focus the input when expanding
        await nextTick();
        searchInput.value?.focus();
      } else {
        // Clear search when collapsing
        searchValue.value = "";
        results.value = [];
        showResults.value = false;
        selectedIndex.value = -1;
      }
    };

    const handleInput = async (event: Event) => {
      const inputValue = (event.target as HTMLInputElement).value;
      if (inputValue.length < 2) {
        results.value = [];
        showResults.value = false;
        return;
      }

      loading.value = true;
      showResults.value = true;
      selectedIndex.value = -1;

      try {
        results.value = [];
        const result = await Axios.get(
          `/api/products/autocomplete/${inputValue}`,
        );
        results.value = result.data;
      } catch (error) {
        console.error("Search error:", error);
        results.value = [];
      } finally {
        loading.value = false;
      }
    };

    const selectResult = (result: any) => {
      searchValue.value = result.shopfront_name;
      showResults.value = false;
      selectedIndex.value = -1;
    };

    const handleBlur = () => {
      // Delay hiding results to allow for clicks on results
      setTimeout(() => {
        // Don't hide if user is clicking on a result
        if (!isClickingResult.value) {
          showResults.value = false;
          selectedIndex.value = -1;
        }
        // Reset the flag
        isClickingResult.value = false;
      }, 200);
    };

    const handleKeydown = (event: KeyboardEvent) => {
      if (!showResults.value || results.value.length === 0) {
        // Handle escape key to collapse search if toggleable
        if (event.key === "Escape" && props.toggleable && isExpanded.value) {
          toggleSearch();
        }
        return;
      }

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          selectedIndex.value = Math.min(
            selectedIndex.value + 1,
            results.value.length - 1,
          );
          break;
        case "ArrowUp":
          event.preventDefault();
          selectedIndex.value = Math.max(selectedIndex.value - 1, 0);
          break;
        case "Enter":
          event.preventDefault();
          if (
            selectedIndex.value >= 0 &&
            selectedIndex.value < results.value.length
          ) {
            selectResult(results.value[selectedIndex.value]);
          }
          break;
        case "Escape":
          showResults.value = false;
          selectedIndex.value = -1;
          if (props.toggleable) {
            toggleSearch();
          }
          break;
      }
    };

    return {
      results,
      searchValue,
      showResults,
      loading,
      selectedIndex,
      searchInput,
      lastIndex,
      isExpanded,
      isClickingResult,
      handleInput,
      selectResult,
      handleBlur,
      handleKeydown,
      toggleSearch,
      navigateToResult,
    };
  },
});
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.2s ease-in-out;
}
</style>
